/**
 * First-touch signup entry-source capture.
 *
 * WHERE a visitor first landed — a SAT/ACT lead-magnet page, a shared quiz, a
 * game, the main diagnostic, organic, etc. — so downstream messaging (SMS
 * sequences) can be tailored to it. Symmetric with ./attribution.ts: captured
 * once on first page load, stashed in sessionStorage (cookie fallback for
 * private mode), and sent with /auth/phone-verify, where the API writes it to
 * `entry_source` for the new user.
 *
 * Two things make this different from attribution:
 *  - it is recorded for EVERY signup (organic included — attribution only has a
 *    row for ad-tagged visits), and
 *  - it derives purely from the landing PATHNAME. Every surface that carries an
 *    id already puts it in the path (`/sat`, `/q/:code`, `/games/:slug`,
 *    `/challenge/:code`), so no per-page wiring is needed — adding a surface is
 *    a single case in `deriveEntrySource`.
 *
 * `TEST_LANDING_BY_SLUG` is imported from configs.js (pure data, NOT the route
 * component) so this stays a lib-level dependency and recognises exactly the
 * test-landing catalog the route itself serves, with zero maintenance.
 */
import { TEST_LANDING_BY_SLUG } from "../routes/testLanding/configs.js";

// Keep in lockstep with the server's allowlist (entry_source.surface CHECK in
// packages/db/src/schema.ts). The server validates, so an unknown value here is
// dropped rather than persisted — but they should match.
export type EntrySurface =
  | "test_landing"
  | "quiz"
  | "game"
  | "diagnostic"
  | "home"
  | "signup_page"
  | "dashboard"
  | "quiz_studio"
  | "challenge"
  | "community"
  | "classroom"
  | "homework"
  | "parent"
  | "funding_check"
  | "direct"
  | "unknown";

export interface EntryContext {
  quiz_code?: string;
  quiz_subject?: string;
  quiz_slug?: string;
  game_slug?: string;
  challenge_code?: string;
  class_code?: string;
  landing_path?: string;
}

export interface EntrySource {
  surface: EntrySurface;
  /** Canonical test-landing slug (e.g. "sat"); only set for `test_landing`. */
  test_slug?: string;
  context?: EntryContext;
}

const STORAGE_KEY = "sa:entry_source";
// Cookie mirror for when sessionStorage is blocked (private mode / quota) —
// mirrors attribution.ts. `:` isn't cookie-name-safe, hence the underscore.
const COOKIE_KEY = "sa_entry_source";

/** Read the raw stash: sessionStorage first, cookie mirror second. */
function readStash(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return raw;
  } catch {
    // sessionStorage blocked — fall through to the cookie.
  }
  try {
    for (const part of document.cookie.split(";")) {
      const eq = part.indexOf("=");
      if (eq === -1) continue;
      if (part.slice(0, eq).trim() === COOKIE_KEY) {
        const v = part.slice(eq + 1).trim();
        return v ? decodeURIComponent(v) : null;
      }
    }
  } catch {
    // document.cookie unavailable too — genuinely nothing stashed.
  }
  return null;
}

/** Write the raw stash to sessionStorage, falling back to a session cookie when
 * storage is blocked. `Secure` rides along on https only (omitted on plain-http
 * local/dev so the fallback isn't silently dropped). Mirrors attribution.ts. */
function writeStash(raw: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, raw);
    return;
  } catch {
    // sessionStorage blocked — mirror into a session cookie instead.
  }
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(raw)}; path=/; SameSite=Lax${secure}`;
}

/**
 * Map a landing pathname to its entry surface. Pure (no storage / no `window`)
 * so it's trivially unit-testable. Every branch returns a valid surface — an
 * unmodelled path falls through to `direct` so every signup is still accounted
 * for (never throws, never returns null).
 */
export function deriveEntrySource(pathname: string): EntrySource {
  const parts = pathname.split("/").filter(Boolean);
  const seg0 = (parts[0] ?? "").toLowerCase();

  // Root and the landing gallery variants (/a … /e) are the generic home
  // funnel. Single-segment only: /c/:code (two segments) is the classroom join
  // deep link, not gallery variant "c".
  if (parts.length === 0 || (parts.length === 1 && ["a", "b", "c", "d", "e"].includes(seg0))) {
    return { surface: "home" };
  }

  // Classroom join deep link (/c/:code) — checked before the switch so it
  // can't be shadowed by the gallery branch above.
  if (seg0 === "c" && parts[1]) {
    return { surface: "homework", context: { class_code: parts[1] } };
  }

  switch (seg0) {
    case "teachers": // /teachers marketing landing (+ /teachers/demo-report)
    case "teach": // /teach dashboard, /teach/signup, class pages
      return { surface: "classroom" };
    case "join": // student class-code entry
    case "homework": // the /homework role gate (mostly students mid-funnel)
      return { surface: "homework" };
    case "family": // parent dashboard / verify / settings
      return { surface: "parent" };
    case "q": // /q/:code[/:slug] — shared quiz (the old attribution blind spot)
      return {
        surface: "quiz",
        context: {
          ...(parts[1] ? { quiz_code: parts[1] } : {}),
          ...(parts[2] ? { quiz_slug: parts[2] } : {}),
        },
      };
    case "quiz": // /quiz/:subject/:slug — semantic, indexable quiz landing
      return {
        surface: "quiz",
        context: {
          ...(parts[1] ? { quiz_subject: parts[1] } : {}),
          ...(parts[2] ? { quiz_slug: parts[2] } : {}),
        },
      };
    case "games": // /games/:slug — game detail
      return { surface: "game", context: parts[1] ? { game_slug: parts[1] } : {} };
    case "challenge": // /challenge/:code
      return {
        surface: "challenge",
        context: parts[1] ? { challenge_code: parts[1] } : {},
      };
    case "quizzes": // Quiz Studio (/quizzes, /quizzes/new)
      return { surface: "quiz_studio" };
    case "diagnostic":
      return { surface: "diagnostic" };
    case "dashboard":
      return { surface: "dashboard" };
    case "community":
      return { surface: "community" };
    case "signup":
      return { surface: "signup_page" };
    case "funding-check": // federal scholarship quiz (ad-only landing)
    case "funding-brief": // its article twin (the A/B); the lead row's `surface` tells them apart
      return { surface: "funding_check" };
  }

  // A single-segment path that is a known test-landing slug is the lead-magnet
  // funnel — the primary thing SMS sequences target on. Uses the same lookup the
  // route serves, so it tracks the catalog automatically.
  if (parts.length === 1 && TEST_LANDING_BY_SLUG[seg0]) {
    return { surface: "test_landing", test_slug: seg0 };
  }

  // Signin / plans / about / a 404 / deep links we don't model: still recorded
  // (as `direct`) so the row exists for every signup.
  return { surface: "direct" };
}

/**
 * Capture the first-touch entry source from the landing pathname and stash it.
 * First touch wins (a later navigation never overwrites — symmetric with
 * attribution). Always carries `landing_path` in context for debugging / future
 * re-derivation. Returns the captured value, or null on a later touch / when
 * both storage layers are blocked.
 */
export function captureEntrySource(
  pathname: string = window.location.pathname,
): EntrySource | null {
  try {
    if (readStash()) return null; // first touch wins
    const derived = deriveEntrySource(pathname);
    const entry: EntrySource = {
      ...derived,
      context: { ...(derived.context ?? {}), landing_path: pathname },
    };
    writeStash(JSON.stringify(entry));
    return entry;
  } catch {
    // Storage blocked and even the cookie setter unreachable — degrade silently.
    return null;
  }
}

/** Read the stashed first-touch entry source (sessionStorage or cookie), or null. */
export function getEntrySource(): EntrySource | null {
  try {
    const raw = readStash();
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && typeof parsed.surface === "string"
      ? (parsed as EntrySource)
      : null;
  } catch {
    return null;
  }
}
